/**
 * Fetches /api/me once after sign-in and shares the result.
 *
 * Replaces useModuleAccess, which fired nine silent token acquisitions on every
 * login to probe nine enterprise app assignments — and then defaulted to
 * granting access when a probe failed or was still in flight.
 *
 * This hook fails closed: until capabilities load, nothing is shown.
 */
import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { createContext, use, type ReactNode } from 'react';

import { api } from '../api/client.js';

export interface VisibleModule {
  id: string;
  name: string;
  description: string | null;
  route: string;
  icon: string | null;
  category: string | null;
  sortOrder: number;
  actions: string[];
}

export interface MeResponse {
  user: { objectId: string; email: string; displayName: string };
  roles: string[];
  permissions: string[];
  modules: VisibleModule[];
  diagnostics: { groupCount: number; groupsFromGraph: boolean };
}

interface PrincipalContextValue {
  me: MeResponse | null;
  isLoading: boolean;
  error: Error | null;
  /** Server-side enforcement is the real gate; this drives what we render. */
  can: (permission: string) => boolean;
  canAny: (...permissions: string[]) => boolean;
}

const PrincipalContext = createContext<PrincipalContextValue | null>(null);

export function PrincipalProvider({ children }: { children: ReactNode }) {
  const { accounts } = useMsal();
  const signedIn = accounts.length > 0;

  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/me'),
    enabled: signedIn,
    staleTime: 60_000,
    retry: 1,
  });

  const permissions = new Set(data?.permissions ?? []);

  const value: PrincipalContextValue = {
    me: data ?? null,
    isLoading: signedIn && isLoading,
    error: (error as Error | null) ?? null,
    can: (permission) => permissions.has(permission),
    canAny: (...list) => list.some((p) => permissions.has(p)),
  };

  return <PrincipalContext value={value}>{children}</PrincipalContext>;
}

export function usePrincipal(): PrincipalContextValue {
  const ctx = use(PrincipalContext);
  if (!ctx) throw new Error('usePrincipal must be used inside PrincipalProvider');
  return ctx;
}
